import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import Swal from "sweetalert2";
import "./fullhouse.css";
import { setBackButtonUrl } from '../uiSlice';
import { sendReport } from "../../functions/sendReport";
import {
  getThemeAssetUrl,
  toCssUrlValue,
  useImageWithFallback,
  FALLBACK_TICKET_IMAGE,
  FALLBACK_LUCKYLOGO_IMAGE,
  themeBackgroundKeyForViewport,
} from "../../functions/themeAssets";

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseObject = (val) => {
  if (val == null) return {};
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return typeof p === "object" && p !== null && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
};

const formatClaimLabel = (key) =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

/** Theme media path from API: relative key or absolute URL → full URL for img/background. */
const buildThemeAssetUrl = (asset) => getThemeAssetUrl(asset);

// Self-contained section: polls theme and re-renders only this block when released words change
function ReleasedWordsSection({ textColor: parentTextColor }) {
  const adminToken = useSelector(selectAdminToken);
  const [releasedValues, setReleasedValues] = useState([]);
  const [textColor, setTextColor] = useState(parentTextColor ?? "#000000");
  const [valueLabel, setValueLabel] = useState("words");
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user, error } = useSelector((state) => state.auth);

  const fetchReleased = React.useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/fetchThemeData`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ currentTheme: null }),
        }
      );
      if (!res.ok) return;
      const json = await res.json().catch(() => ({}));
      const data = json?.data ?? {};
      const valuesArray = parseArray(data.values_array ?? []);
      const shuffle = parseArray(data.shuffle ?? []);
      const launchpos = parseInt(data.launchpos);
      const numlimit = parseInt(data.numlimit);
      const releasedCount = Math.min(launchpos * numlimit, shuffle.length);
      const out = [];
      for (let i = 0; i < releasedCount && i < shuffle.length; i++) {
        const idx = shuffle[i];
        const pos = (typeof idx === "string" ? parseInt(idx, 10) : idx) - 1;
        const v = valuesArray[pos];
        out.push(v != null ? String(v) : "");
      }
      setReleasedValues(out);
      if (data.text_color != null) setTextColor(data.text_color);
      setValueLabel(
        valuesArray.length > 0 && isNaN(Number(valuesArray[0])) ? "words" : "numbers"
      );
    } catch {
      // ignore
    }
  }, [adminToken]);

  useEffect(() => {
    fetchReleased();
    const interval = setInterval(fetchReleased, 4000);
    return () => clearInterval(interval);
  }, [fetchReleased]);

  const perRow = 6;
  const rows = [];
  for (let r = 0; r < releasedValues.length; r += perRow) {
    const rowValues = releasedValues.slice(r, r + perRow);
    while (rowValues.length < perRow) rowValues.push("");
    rows.push(
      <tr key={r}>
        {rowValues.map((v, i) => (
          <td key={i}>
            {v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : ""}
          </td>
        ))}
      </tr>
    );
  }

  return (
    <div className="fullhouse-released">
      <div style={{ color: textColor, marginTop: 20, fontSize: 20, textAlign: "center" }}>
        Released {valueLabel} will appear here
      </div>
      {rows.length > 0 && (
        <table className="fullhouse-released-table">
          <tbody>{rows}</tbody>
        </table>
      )}
    </div>
  );
}

function FullHouse() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);
  const { status, user, error } = useSelector((state) => state.auth);
  
  const valuesArray = useMemo(() => parseArray(themeData?.values_array ?? []), [themeData]);
  const shuffle = useMemo(() => parseArray(themeData?.shuffle ?? []), [themeData]);
  const maxvalue = (parseInt(themeData?.maxvalue));
  const claimsConfig = useMemo(() => parseObject(themeData?.claims), [themeData]);
  const enabledClaims = useMemo(
    () => Object.keys(claimsConfig).filter((k) => claimsConfig[k]),
    [claimsConfig]
  );
  const rules = useMemo(() => parseArray(themeData?.rules ?? []).filter(Boolean), [themeData]);
  const autoGenerate = themeData?.auto_generate === "true" || themeData?.auto_generate === true;
  const preventSubmit = themeData?.prevent_submit === "true" || themeData?.prevent_submit === true;
  const preventClaim = themeData?.prevent_claim === "true" || themeData?.prevent_claim === true;
  const displayLeaderboard =
    themeData?.display_leaderboard === "true" || themeData?.display_leaderboard === true;

  const tableColor1 = themeData?.table_color1 ?? "#ffffff";
  const tableColor2 = themeData?.table_color2 ?? "#e0e0e0";
  const textColor = themeData?.text_color ?? "#000000";
  const buttonColor = themeData?.button_color ?? "#6b91e8";
  const buttonTextColor = themeData?.button_text_color ?? "#ffffff";
  const leaderboardColor = themeData?.leaderboard_color ?? "#000000";

  const bgImage = useMemo(() => {
    const bgKey = themeBackgroundKeyForViewport(themeData);
    return bgKey ? buildThemeAssetUrl(bgKey) : "";
  }, [themeData]);

  const ticketBgUrl = useImageWithFallback(
    buildThemeAssetUrl(typeof themeData?.ticket === "string" ? themeData.ticket : ""),
    FALLBACK_TICKET_IMAGE
  );
  const luckyLogoUrl = useImageWithFallback(
    buildThemeAssetUrl(themeData?.luckylogo),
    FALLBACK_LUCKYLOGO_IMAGE
  );

  const isWords = valuesArray.length > 0 && isNaN(Number(valuesArray[0]));
  const valueLabel = isWords ? "words" : "numbers";

  const [ticketCells, setTicketCells] = useState(Array(12).fill(""));
  const [selectedWordIndices, setSelectedWordIndices] = useState([]);
  const [showTicketView, setShowTicketView] = useState(false);
  const [submittedNumbers, setSubmittedNumbers] = useState([]);
  const [highlightPositions, setHighlightPositions] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState("");
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [ticketCheckDone, setTicketCheckDone] = useState(false);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [status, user]);

  // If user already has a ticket, show ticket + claim view only
  useEffect(() => {
    if (!themeData || !adminToken || !user?.userId) {
      setTicketCheckDone(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getMyTicket`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId ?? user.id,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.hasTicket) {
          if (user?.sessionId === "demobypass" || user?.role === "demobypass") {
            window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
            return;
          }
          const numbers = Array.isArray(data.numbers) ? data.numbers : [];
          const padded = [...numbers];
          while (padded.length < 12) padded.push("");
          setSubmittedNumbers(padded.slice(0, 12));
          const highlight = Array.isArray(data.highlight) ? data.highlight : [];
          setHighlightPositions(highlight.filter((h) => h >= 0 && h < 12));
          setShowTicketView(true);
        }
      } catch {
        if (!cancelled) setTicketCheckDone(true);
        return;
      }
      if (!cancelled) setTicketCheckDone(true);
    })();
    return () => { cancelled = true; };
  }, [themeData, adminToken, user]);

  const toggleWord = (index) => {
    setSelectedWordIndices((prev) => {
      const i = prev.indexOf(index);
      if (i >= 0) {
        const next = [...prev];
        next.splice(i, 1);
        const cells = [...ticketCells];
        const pos = prev.indexOf(index);
        for (let j = pos; j < 11; j++) cells[j] = cells[j + 1];
        cells[11] = "";
        setTicketCells(cells);
        return next;
      }
      if (prev.length >= 12) {
        Swal.fire("", `Only 12 ${valueLabel} can be selected`, "info");
        return prev;
      }
      const val = valuesArray[index];
      const str = val != null ? String(val).charAt(0).toUpperCase() + String(val).slice(1) : "";
      const next = [...prev, index];
      const cells = [...ticketCells];
      const firstEmpty = cells.findIndex((c) => !c);
      if (firstEmpty >= 0) cells[firstEmpty] = str;
      setTicketCells(cells);
      return next;
    });
  };

  const generateRandom = () => {
    const count = Math.min(maxvalue, valuesArray.length);
    if (count < 12) return;
    const indices = [];
    while (indices.length < 12) {
      const r = Math.floor(Math.random() * count);
      if (!indices.includes(r)) indices.push(r);
    }
    const cells = indices.map((i) => {
      const v = valuesArray[i];
      return v != null ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : "";
    });
    setTicketCells(cells);
    setSelectedWordIndices(indices);
    Swal.fire("Ticket Generated", "", "success");
  };

  const handleSubmit = async () => {
    const filled = ticketCells.filter((c) => c.trim() !== "");
    if (filled.length !== 12) {
      Swal.fire("", `You must select all 12 ${valueLabel}`, "error");
      return;
    }
    const numbers = [...ticketCells];
    setSubmitLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/bingoSubmit`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          numbers,
          userId: user?.userId ?? user?.id,
          email: user?.email ?? "",
          name: user?.name ?? "",
          currentTheme: themeData?.themename ?? themeData?.themeName ?? null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMessage = data.error || "Failed to submit ticket";
        if (errorMessage.includes("already") || errorMessage.includes("Already")) {
          Swal.fire("", "You have already claimed this prize.", "error");
        } else if (errorMessage.includes("maximum") || errorMessage.includes("winners") || errorMessage.includes("reached") || errorMessage.includes("prizes claimed") || errorMessage.includes("category")) {
          Swal.fire("", "The maximum number of winners for this line has already been reached.", "error");
        } else {
          Swal.fire("", errorMessage, "error");
        }
        return;
      }
      setSubmittedNumbers(numbers);
      setShowTicketView(true);
      setHighlightPositions([]);
      if (user?.sessionId === "demobypass" || user?.role === "demobypass") {
        window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
        return;
      }
    } catch (e) {
      Swal.fire("", e.message || "Failed to submit ticket", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleHighlight = async (pos) => {
    const next = highlightPositions.includes(pos)
      ? highlightPositions.filter((p) => p !== pos)
      : [...highlightPositions, pos];
    setHighlightPositions(next);
    if (!adminToken || !user?.userId) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateSelection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId ?? user.id,
          tickit_select: next,
        }),
      });
    } catch {
      // persist on next toggle or reload
    }
  };

  const handleSubmitClaim = async () => {
    if (!selectedClaim) return;
    const userId = user?.userId ?? user?.id ?? user?.email ?? "";
    const userIdStr = userId != null ? String(userId) : "";
    if (!userIdStr.trim()) {
      Swal.fire("", "Please log in again to submit a claim.", "error");
      return;
    }
    setClaimLoading(true);
    try {
      if (!adminToken || !user?.userId) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/submitClaim`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          claim: selectedClaim,
          userId: userIdStr,
          currentTheme: themeData?.themename ?? themeData?.themeName ?? null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMessage = data.error || "Failed to submit claim";
        if (
          errorMessage.includes("another category") ||
          /multiple claims/i.test(errorMessage)
        ) {
          Swal.fire("", "Multiple claims not allowed.", "error");
        } else if (errorMessage.includes("already") || errorMessage.includes("Already")) {
          Swal.fire("", `You’ve already claimed ${formatClaimLabel(selectedClaim)}. Keep going, there’s more to play for!`, "error");
        } else if (errorMessage.includes("maximum") || errorMessage.includes("winners") || errorMessage.includes("reached") || errorMessage.includes("prizes claimed") || errorMessage.includes("category")) {
          Swal.fire("", "The maximum number of winners for this line has already been reached.", "error");
        } else {
          Swal.fire("", errorMessage, "error");
        }
        return;
      }

      // Claim accepted: send report for guest or normal user
      const claimname = selectedClaim;
      const isGuest = user?.role === "GUEST_USER";
      const userData = user;
      const winnerInfo = user;

      const reportData = {
        sessionId: user.sessionId,
        role: user.role,
        token: user.token || null,
        name: user.name,
        userId: user.userId,
        gameId: user.gameId || "",
        organizationId: user.organizationId,
        points: 0,
        time: claimname,
        reportId: user.reportId || null,
        ans: claimname,
      };
      console.log(reportData);
      try {
        await sendReport(reportData);
      } catch (reportErr) {
        console.warn("Report request failed:", reportErr);
      }

      await Swal.fire("", data.message || "Claim Accepted", "success");
      setSelectedClaim("");
    } catch (e) {
      Swal.fire("", e.message || "Failed to submit claim", "error");
    } finally {
      setClaimLoading(false);
    }
  };

  if (themeStatus === "loading" || !themeData || !ticketCheckDone) {
    return (
      <div className="fullhouse-page">
        <div className="fullhouse-loader">
          <div className="quiz-loader" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fullhouse-page body-background"
      style={bgImage ? { backgroundImage: toCssUrlValue(bgImage) } : {}}
    >
      <div className="fullhouse-top-bar">
        {/* <button
          type="button"
          className="fullhouse-back-btn"
          style={{ backgroundColor: buttonColor, color: buttonTextColor }}
          onClick={() => navigate("/rules")}
        >
          Back
        </button> */}
        <button
          type="button"
          className="fullhouse-rules-btn"
          style={{ backgroundColor: buttonColor, color: buttonTextColor }}
          onClick={() => setRulesModalOpen(true)}
        >
          Rules
        </button>
      </div>

      {rulesModalOpen && (
        <div className="fullhouse-modal-overlay" onClick={() => setRulesModalOpen(false)}>
          <div className="fullhouse-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fullhouse-modal-title" style={{color: themeData.text_color}}>Rules</div>
            <ol className="fullhouse-modal-list">
              {rules.map((r, i) => (
                <li key={i} style={{color: themeData.text_color}}>{r}</li>
              ))}
            </ol>
            <button
              type="button"
              className="fullhouse-modal-close"
              onClick={() => setRulesModalOpen(false)}
              style={{backgroundColor: themeData.button_color, color: themeData.button_text_color}} >
              Close
            </button>
          </div>
        </div>
      )}

      {!showTicketView ? (
        <>
        <div className="fullhouse-create">
          <div className="fullhouse-lucky-logo">
            <img
              src={luckyLogoUrl}
              alt="Lucky"
              style={{ maxWidth: "240px", marginTop: 15 }}
            />
          </div>
          <p className="fullhouse-follow" style={{ color: textColor }}>
            {themeData?.follow_text}
          </p>
          <div className="fullhouse-steps" style={{ color: textColor }}>
            <span className="step-highlight">Step 1 : </span>
            {themeData?.step1_text}
            <br />
            <span className="step-highlight">Step 2 : </span>
            {themeData?.step2_text}
          </div>

          <div className="fullhouse-wordsdiv">
            <table className="fullhouse-words-table">
              <tbody>
                {(() => {
                  const rows = [];
                  for (let i = 0; i < (maxvalue || valuesArray.length) && i < valuesArray.length; i++) {
                    if (i % 5 === 0) rows.push([]);
                    const val = valuesArray[i];
                    const str = val != null ? String(val).charAt(0).toUpperCase() + String(val).slice(1) : "";
                    const selected = selectedWordIndices.includes(i);
                    rows[rows.length - 1].push(
                      <td
                        key={i}
                        className={selected ? "fullhouse-word selected" : "fullhouse-word"}
                        onClick={() => toggleWord(i)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && toggleWord(i)}
                      >
                        <span>{str}</span>
                      </td>
                    );
                  }
                  return rows.map((row, ri) => (
                    <tr key={ri}>
                      {row}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          <div className="fullhouse-gen-wrap">
            <button
              type="button"
              className="fullhouse-gen-btn"
              style={{ backgroundColor: buttonColor, color: buttonTextColor }}
              onClick={() => {
                if (autoGenerate) return;
                generateRandom();
              }}
              disabled={autoGenerate}
            >
              Click To Generate Automatic Ticket
            </button>
          </div>
        </div>

        <div
          className="fullhouse-ticket-wrap"
          style={{
            backgroundImage: ticketBgUrl ? toCssUrlValue(ticketBgUrl) : "none",
          }}
        >
          <table className="fullhouse-ticket-table">
            <tbody>
              <tr>
                {[0, 1, 2, 3].map((i) => (
                  <React.Fragment key={i}>
                    <td
                      className="fullhouse-ticket-cell white"
                      style={{
                        backgroundColor: tableColor1,
                        color: textColor,
                      }}
                    >
                      {ticketCells[i] || "\u00a0"}
                    </td>
                    <td className="fullhouse-ticket-cell black" style={{ backgroundColor: tableColor2 }} />
                  </React.Fragment>
                ))}
              </tr>
              <tr>
                {[4, 5, 6, 7].map((i) => (
                  <React.Fragment key={i}>
                    <td className="fullhouse-ticket-cell black" style={{ backgroundColor: tableColor2 }} />
                    <td
                      className="fullhouse-ticket-cell white"
                      style={{
                        backgroundColor: tableColor1,
                        color: textColor,
                      }}
                    >
                      {ticketCells[i] || "\u00a0"}
                    </td>
                  </React.Fragment>
                ))}
              </tr>
              <tr>
                {[8, 9, 10, 11].map((i) => (
                  <React.Fragment key={i}>
                    <td
                      className="fullhouse-ticket-cell white"
                      style={{
                        backgroundColor: tableColor1,
                        color: textColor,
                      }}
                    >
                      {ticketCells[i] || "\u00a0"}
                    </td>
                    <td className="fullhouse-ticket-cell black" style={{ backgroundColor: tableColor2 }} />
                  </React.Fragment>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="fullhouse-create">
          <div className="fullhouse-formdiv">
            {preventSubmit ? (
              <div className="fullhouse-notice">Submit Ticket are Disabled by Admin</div>
            ) : (
              <button
                type="button"
                className="fullhouse-submit-btn"
                style={{
                  backgroundColor: buttonColor,
                  color: buttonTextColor,
                }}
                onClick={handleSubmit}
                disabled={submitLoading}
              >
                {submitLoading ? "Submitting..." : "Submit"}
              </button>
            )}
          </div>
        </div>
        </>
      ) : (
        <div className="fullhouse-ticketshow">
          <p className="fullhouse-thankyou" style={{ color: textColor }}>
            Thank you for creating your ticket.
            <br />
            Highlight the {valueLabel} by clicking on your ticket one at a time.
          </p>

          <div 
            className="fullhouse-final-ticket"
            style={{
              backgroundImage: ticketBgUrl ? toCssUrlValue(ticketBgUrl) : "none",
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              padding: "26px 80px",
            }}
          >
            <table className="fullhouse-final-table">
              <tbody>
                <tr>
                  {[0, 1, 2, 3].map((i) => (
                    <React.Fragment key={i}>
                      <td
                        className={`fullhouse-final-cell ${highlightPositions.includes(i) ? "highlight" : ""}`}
                        style={{
                          backgroundColor: highlightPositions.includes(i) ? "#ffeb3b" : tableColor1,
                          color: textColor,
                        }}
                        onClick={() => toggleHighlight(i)}
                        role="button"
                        tabIndex={0}
                      >
                        {submittedNumbers[i] || "\u00a0"}
                      </td>
                      <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor2 }} />
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor2 }} />
                  {[4, 5, 6, 7].map((i, idx) => (
                    <React.Fragment key={i}>
                      <td
                        className={`fullhouse-final-cell ${highlightPositions.includes(i) ? "highlight" : ""}`}
                        style={{
                          backgroundColor: highlightPositions.includes(i) ? "#ffeb3b" : tableColor1,
                          color: textColor,
                        }}
                        onClick={() => toggleHighlight(i)}
                        role="button"
                        tabIndex={0}
                      >
                        {submittedNumbers[i] || "\u00a0"}
                      </td>
                      {idx < 3 && <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor2 }} />}
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  {[8, 9, 10, 11].map((i) => (
                    <React.Fragment key={i}>
                      <td
                        className={`fullhouse-final-cell ${highlightPositions.includes(i) ? "highlight" : ""}`}
                        style={{
                          backgroundColor: highlightPositions.includes(i) ? "#ffeb3b" : tableColor1,
                          color: textColor,
                        }}
                        onClick={() => toggleHighlight(i)}
                        role="button"
                        tabIndex={0}
                      >
                        {submittedNumbers[i] || "\u00a0"}
                      </td>
                      <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor2 }} />
                    </React.Fragment>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <ReleasedWordsSection textColor={textColor} />

          <div className="fullhouse-claim-section">
            <div style={{ color: textColor, marginTop: 20, fontSize: 18, textAlign: "center" }}>
              Claim
            </div>
            {enabledClaims.length === 0 || preventClaim ? (
              <div style={{ textAlign: "center" }}>
                <select
                  className="fullhouse-select"
                  disabled
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                >
                  <option>Select claim</option>
                </select>
                <br />
                <div className="fullhouse-notice">
                  {enabledClaims.length === 0 ? "No claims available" : "Submit Claim are Disabled by admin"}
                </div>
              </div>
            ) : (
              <>
                <select
                  className="fullhouse-select"
                  value={selectedClaim}
                  onChange={(e) => setSelectedClaim(e.target.value)}
                >
                  <option value="">Select claim</option>
                  {enabledClaims.map((key) => (
                    <option key={key} value={key}>
                      {formatClaimLabel(key)}
                    </option>
                  ))}
                </select>
                <br />
                <button
                  type="button"
                  className="fullhouse-claim-btn"
                  style={{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }}
                  onClick={handleSubmitClaim}
                  disabled={claimLoading || !selectedClaim}
                >
                  {claimLoading ? "Submitting..." : "Submit Claim"}
                </button>
              </>
            )}
            {displayLeaderboard && (
              <button
                type="button"
                className="fullhouse-leaderboard-btn"
                style={{ color: buttonTextColor, backgroundColor: buttonColor }}
                onClick={() => navigate("/leaderboard")}
              >
                View Leaderboard
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FullHouse;
